import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getCatalogLanguagesV2, type CatalogItemDto } from '@/lib/api/v2/catalog'
import { patchProjectPreferredLanguageV2 } from '@/lib/api/v2/projects'

interface ProjectLanguageControlProps {
  projectId: string
  /** The project's current document-language snapshot. */
  currentLanguageId: string
  /** The customer's default language — used only to surface a divergence hint. */
  customerLanguageId?: string | null
  canEdit: boolean
  /** Called after a successful PATCH so the parent can refresh project details. */
  onUpdated: (newLanguageId: string) => void
}

const sameLanguage = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

/**
 * Compact control to view / change a project's document language.
 * Hidden entirely when the organization supports a single language —
 * the language picker is only meaningful for multilingual orgs.
 */
export function ProjectLanguageControl({
  projectId,
  currentLanguageId,
  customerLanguageId,
  canEdit,
  onUpdated,
}: ProjectLanguageControlProps) {
  const [languages, setLanguages] = useState<CatalogItemDto[]>([])
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    getCatalogLanguagesV2()
      .then((langs) => {
        if (!cancelled) setLanguages(langs)
      })
      .catch(() => {
        // Non-blocking: if languages can't load the control simply stays hidden.
      })
      .finally(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Single-language orgs get no picker — nothing to choose.
  if (!loaded || languages.length < 2) return null

  const labelOf = (id?: string | null) =>
    languages.find((l) => sameLanguage(l.id, id))?.label ?? id ?? ''

  const diverges =
    !!customerLanguageId && !sameLanguage(currentLanguageId, customerLanguageId)

  const handleChange = async (next: string) => {
    if (sameLanguage(next, currentLanguageId)) return
    setSaving(true)
    try {
      const res = await patchProjectPreferredLanguageV2(projectId, next)
      onUpdated(res.preferredLanguageId)
      toast.success('Langue du document mise à jour')
    } catch (error) {
      toast.error('Impossible de mettre à jour la langue du document')
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Langue du document
      </p>
      <div className="mt-1 flex items-center gap-2">
        <Select
          value={currentLanguageId}
          onValueChange={handleChange}
          disabled={!canEdit || saving}
        >
          <SelectTrigger className="h-8 w-[150px]" data-testid="project-language-select">
            <SelectValue placeholder="Sélectionner" />
          </SelectTrigger>
          <SelectContent>
            {languages.map((lang) => (
              <SelectItem key={lang.id} value={lang.id!}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {diverges && (
          <Badge
            variant="outline"
            className="text-muted-foreground font-normal"
            title={`Le projet est en ${labelOf(currentLanguageId)}, le client par défaut en ${labelOf(customerLanguageId)}. Cette divergence est volontaire — change la langue du projet pour la resynchroniser.`}
          >
            Client en {labelOf(customerLanguageId)}
          </Badge>
        )}
      </div>
    </div>
  )
}
